import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onAddCRMUser: (email: string, role: 'admin' | 'super_admin' | 'crm_user' | 'charging_user') => Promise<boolean>;
  onAddPortalUser: (email: string, role: string, linkedId: string) => Promise<boolean>;
  onAddChargingUser: (email: string, password: string, displayName?: string) => Promise<boolean>;
}

export const AddUserModal = ({
  open,
  onClose,
  onAddCRMUser,
  onAddPortalUser,
  onAddChargingUser,
}: AddUserModalProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<string>('crm_user');
  const [linkedId, setLinkedId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (type: 'crm' | 'portal' | 'charging') => {
    setLoading(true);
    let success = false;

    try {
      if (type === 'crm') {
        success = await onAddCRMUser(email, role as any);
      } else if (type === 'portal') {
        success = await onAddPortalUser(email, role, linkedId);
      } else if (type === 'charging') {
        success = await onAddChargingUser(email, password, displayName);
      }

      if (success) {
        setEmail('');
        setPassword('');
        setDisplayName('');
        setRole('crm_user');
        setLinkedId('');
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gebruiker toevoegen</DialogTitle>
          <DialogDescription>
            Kies het type gebruiker en vul de gegevens in.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="crm" className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="crm">CRM</TabsTrigger>
            <TabsTrigger value="portal">Portal</TabsTrigger>
            <TabsTrigger value="charging">Charging</TabsTrigger>
          </TabsList>

          <TabsContent value="crm" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="crm-email">Email</Label>
              <Input
                id="crm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="gebruiker@greenvision.nl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="crm-role">Rol</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="crm-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm_user">CRM Gebruiker</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => handleSubmit('crm')}
              disabled={loading || !email}
              className="w-full"
            >
              {loading ? 'Bezig...' : 'Uitnodiging versturen'}
            </Button>
          </TabsContent>

          <TabsContent value="portal" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="portal-email">Email</Label>
              <Input
                id="portal-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="inspector@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="portal-role">Rol</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="portal-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspector">Inspector</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linked-id">Linked ID (Contact/Org UUID)</Label>
              <Input
                id="linked-id"
                value={linkedId}
                onChange={(e) => setLinkedId(e.target.value)}
                placeholder="UUID van gekoppeld contact/organisatie"
              />
            </div>

            <Button
              onClick={() => handleSubmit('portal')}
              disabled={loading || !email || !linkedId}
              className="w-full"
            >
              {loading ? 'Bezig...' : 'Portal gebruiker aanmaken'}
            </Button>
          </TabsContent>

          <TabsContent value="charging" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="charging-email">Email</Label>
              <Input
                id="charging-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="gebruiker@greenvision-charging.nl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="charging-password">Wachtwoord</Label>
              <Input
                id="charging-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimaal 6 karakters"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="charging-name">Naam (optioneel)</Label>
              <Input
                id="charging-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Volledige naam"
              />
            </div>

            <Button
              onClick={() => handleSubmit('charging')}
              disabled={loading || !email || !password || password.length < 6}
              className="w-full"
            >
              {loading ? 'Bezig...' : 'Charging gebruiker aanmaken'}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
